import {
  isTripMasterSurface,
  resolveTripMasterPublicSiteToken,
  TRIPMASTER_CONTACT_EMAIL,
  TRIPMASTER_PUBLIC_SITE_TOKEN,
} from "../../surfaces/tripMaster/tripMasterEnv";
import { isTripMasterCleanPublicPath } from "../../surfaces/tripMaster/tripMasterUrls";

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return null;
}

export function resolvePublicSiteId(siteIdentity) {
  return firstNonEmpty(
    siteIdentity?.public_site_token,
    siteIdentity?.site_token,
    siteIdentity?.site_id,
    siteIdentity?.public_site_id
  );
}

const INVALID_LOGO_LITERALS = new Set(["null", "none", "undefined", "n/a", "na"]);

/**
 * Normalizes a public-site brand logo value for <img src>.
 * Unlike the generic agent-image normalizer, the brand header logo is the
 * site's identity and must NOT be dropped by a perf size cap — agents legitimately
 * upload large PNG logos. Accepts data: / http(s) / local path / raw base64.
 * @param {unknown} raw
 * @returns {string|null}
 */
export function normalizePublicSiteLogoValue(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s || INVALID_LOGO_LITERALS.has(s.toLowerCase())) return null;
  if (s.startsWith("data:image")) return s;
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/") || s.startsWith("./") || s.startsWith("../")) return s;
  return `data:image/png;base64,${s}`;
}

/**
 * לוגו מותג בסייט הציבורי. סדר מקורות (SSOT):
 *   branding.logo_url → branding.logo → top-level logo → שאר אליאסים.
 * מחזיר את הערך הגולמי (ללא נורמליזציה) או null.
 */
export function resolvePublicSiteBrandLogoUrl(siteIdentity) {
  if (!siteIdentity || typeof siteIdentity !== "object") return null;
  const branding = siteIdentity.branding;
  return firstNonEmpty(
    branding?.logo_url,
    branding?.logo,
    branding?.logoUrl,
    branding?.brandLogo,
    branding?.brand_logo,
    siteIdentity.logo,
    siteIdentity.logo_url,
    siteIdentity.logoUrl,
    siteIdentity.brand_logo,
    siteIdentity.public_brand_logo
  );
}

export function resolvePublicSiteBrandLogoSource(siteIdentity) {
  if (!siteIdentity || typeof siteIdentity !== "object") return null;
  const branding = siteIdentity.branding;
  if (firstNonEmpty(branding?.logo_url)) return "branding.logo_url";
  if (firstNonEmpty(branding?.logo)) return "branding.logo";
  if (firstNonEmpty(branding?.logoUrl)) return "branding.logoUrl";
  if (firstNonEmpty(branding?.brandLogo)) return "branding.brandLogo";
  if (firstNonEmpty(branding?.brand_logo)) return "branding.brand_logo";
  if (firstNonEmpty(siteIdentity.logo)) return "logo";
  if (firstNonEmpty(siteIdentity.logo_url)) return "logo_url";
  if (firstNonEmpty(siteIdentity.logoUrl)) return "logoUrl";
  if (firstNonEmpty(siteIdentity.brand_logo)) return "brand_logo";
  if (firstNonEmpty(siteIdentity.public_brand_logo)) return "public_brand_logo";
  return null;
}

export function resolvePublicSiteBrandDisplayType(siteIdentity) {
  if (!siteIdentity || typeof siteIdentity !== "object") return null;
  return firstNonEmpty(
    siteIdentity.brand_display_type,
    siteIdentity.branding?.brand_display_type
  );
}

export function resolvePublicSiteBrandDisplayName(siteIdentity) {
  if (!siteIdentity || typeof siteIdentity !== "object") return null;
  const branding = siteIdentity.branding;
  return firstNonEmpty(
    siteIdentity.agency_name,
    siteIdentity.agencyName,
    siteIdentity.business_name,
    siteIdentity.businessName,
    siteIdentity.company_name,
    branding?.agency_name,
    branding?.agencyName,
    branding?.business_name,
    branding?.businessName
  );
}

/**
 * Public homepage contact email. TripMaster always uses fly@tripmaster.co.il
 * (forwards to ops); other public sites keep site_settings.email.
 */
export function resolvePublicSiteContactEmail(siteIdentity, options) {
  const token = resolvePublicSiteId(siteIdentity);
  if (token === TRIPMASTER_PUBLIC_SITE_TOKEN || isTripMasterSurface(options)) {
    return TRIPMASTER_CONTACT_EMAIL;
  }
  return firstNonEmpty(siteIdentity?.site_settings?.email);
}

/**
 * SSOT לכותרת הטאב/SEO של הסייט הציבורי. סדר העדפה:
 *   site_settings.meta_title → site_settings.site_name →
 *   branding.site_title → branding.title → site_title → agency_name → companyname.
 * מחזיר null אם אין כותרת סוכנות (אז ה-SeoMetaGuard נופל ל-HotelsRiver fallback).
 * @param {object|null} siteIdentity
 * @returns {string|null}
 */
export function resolvePublicSiteTitle(siteIdentity) {
  if (!siteIdentity || typeof siteIdentity !== "object") return null;
  const branding = siteIdentity.branding;
  const siteSettings = siteIdentity.site_settings;
  return firstNonEmpty(
    siteSettings?.meta_title,
    siteSettings?.site_name,
    branding?.site_title,
    branding?.siteTitle,
    branding?.title,
    branding?.meta_title,
    siteIdentity.site_title,
    siteIdentity.siteTitle,
    siteIdentity.meta_title,
    resolvePublicSiteBrandDisplayName(siteIdentity),
    siteIdentity.companyname,
    siteIdentity.companyName,
    branding?.companyname,
    branding?.companyName
  );
}

/**
 * SSOT לתיאור SEO של הסייט הציבורי.
 * @param {object|null} siteIdentity
 * @returns {string|null}
 */
export function resolvePublicSiteDescription(siteIdentity) {
  if (!siteIdentity || typeof siteIdentity !== "object") return null;
  const branding = siteIdentity.branding;
  const siteSettings = siteIdentity.site_settings;
  return firstNonEmpty(
    siteSettings?.meta_description,
    siteSettings?.site_tagline,
    siteSettings?.about_text,
    branding?.meta_description,
    branding?.description,
    siteIdentity.meta_description,
    siteIdentity.description
  );
}

/**
 * SSOT למילות מפתח SEO של הסייט הציבורי.
 * @param {object|null} siteIdentity
 * @returns {string|null}
 */
export function resolvePublicSiteKeywords(siteIdentity) {
  if (!siteIdentity || typeof siteIdentity !== "object") return null;
  const siteSettings = siteIdentity.site_settings;
  const branding = siteIdentity.branding;
  return firstNonEmpty(
    siteSettings?.meta_keywords,
    branding?.meta_keywords,
    siteIdentity.meta_keywords
  );
}

/**
 * מטא SEO מלא לסייט ציבורי של סוכן (title / description / keywords).
 * @param {object|null} siteIdentity
 * @returns {{ title: string|null, description: string|null, keywords: string|null }}
 */
export function resolvePublicSiteSeoMeta(siteIdentity) {
  return {
    title: resolvePublicSiteTitle(siteIdentity),
    description: resolvePublicSiteDescription(siteIdentity),
    keywords: resolvePublicSiteKeywords(siteIdentity),
  };
}

/**
 * SSOT לוגו ה-header של הסייט הציבורי.
 * סדר: branding.logo_url → branding.logo → logo → (אם agency_text ואין לוגו) שם סוכנות → HotelsRiver fallback.
 * @param {object|null} siteIdentity
 * @param {{ fallbackLogoUrl?: string|null }} [opts]
 * @returns {{ kind: "logo"|"agency_text"|"fallback", src: string|null, text: string|null, source: string, brandDisplayType: string|null, agencyName: string|null }}
 */
export function resolvePublicSiteHeaderLogo(siteIdentity, opts = {}) {
  const fallbackLogoUrl = opts.fallbackLogoUrl ?? null;
  const brandDisplayType = resolvePublicSiteBrandDisplayType(siteIdentity);
  const agencyName = resolvePublicSiteBrandDisplayName(siteIdentity);

  const agentLogo = normalizePublicSiteLogoValue(resolvePublicSiteBrandLogoUrl(siteIdentity));
  if (agentLogo) {
    return {
      kind: "logo",
      src: agentLogo,
      text: null,
      source: resolvePublicSiteBrandLogoSource(siteIdentity) || "agent_logo",
      brandDisplayType: brandDisplayType || "agent_logo",
      agencyName,
    };
  }

  if (brandDisplayType === "agency_text" && agencyName) {
    return {
      kind: "agency_text",
      src: null,
      text: agencyName,
      source: "agency_text",
      brandDisplayType,
      agencyName,
    };
  }

  return {
    kind: "fallback",
    src: fallbackLogoUrl,
    text: null,
    source: "hotelsriver_fallback",
    brandDisplayType: brandDisplayType || "hotelsriver_fallback",
    agencyName,
  };
}

/**
 * מחירון HR לציבור: מגיע מ־GET /api/public-sites/by-token/… (שדה agent_hotel_pricing).
 * חייב מספר סופי (כולל 0%) — אחרת null (אין תמחור ציבור תקין).
 */
export function resolveAgentHotelPricingFromSiteIdentity(siteIdentity) {
  const raw =
    siteIdentity?.agent_hotel_pricing ?? siteIdentity?.agentHotelPricing ?? null;
  if (!raw || typeof raw !== "object") return null;
  const hr = Number(raw.hotelHrCommissionPercentage ?? raw.hotel_hr_commission_percentage);
  const fixed = Number(raw.fixedHotelCommission ?? raw.fixed_hotel_commission) || 0;
  if (!Number.isFinite(hr)) return null;
  return {
    hotelHrCommissionPercentage: hr,
    fixedHotelCommission: Number.isFinite(fixed) && fixed > 0 ? fixed : 0,
  };
}

export const PUBLIC_SITE_TOKEN_STORAGE_KEY = "hr_agent_public_site_token";
export const PUBLIC_SITE_ID_STORAGE_KEY = PUBLIC_SITE_TOKEN_STORAGE_KEY;
export const PUBLIC_SITE_REF_STORAGE_KEY = PUBLIC_SITE_TOKEN_STORAGE_KEY;
// v2: bumped to invalidate any pre-branding-fix cached identity (logo source mapping changed).
export const PUBLIC_SITE_IDENTITY_STORAGE_KEY = "hr_public_site_identity_v2";
const PUBLIC_SITE_IDENTITY_LEGACY_STORAGE_KEYS = ["hr_public_site_identity_v1"];
export const PUBLIC_SITE_IDENTITY_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export function isNumericPublicSiteRef(value) {
  return /^\d+$/.test(String(value || "").trim());
}

export function normalizePublicSiteToken(...values) {
  const token = firstNonEmpty(...values);
  if (!token) return null;
  return isNumericPublicSiteRef(token) ? null : token;
}

export function normalizePublicAgentId(...values) {
  const raw = firstNonEmpty(...values);
  if (!raw) return null;
  const normalized = String(raw).trim();
  return /^\d+$/.test(normalized) ? normalized : null;
}

function stripLocalePrefixFromPath(pathname) {
  const path = String(pathname || "");
  return path.replace(/^\/(he|en|us|el)(?=\/|$)/, "") || "/";
}

export function isPublicSiteRoute(pathname) {
  const path = stripLocalePrefixFromPath(pathname);
  return (
    (isTripMasterSurface() && isTripMasterCleanPublicPath(path)) ||
    path === "/vacation-packages" ||
    path.startsWith("/vacation-packages/") ||
    path === "/vacation_packages" ||
    path.startsWith("/vacation_packages/") ||
    path.startsWith("/agent/")
  );
}

/** Set while PublicSiteProvider is mounted — shopping chrome for public + TripMaster. */
export const PUBLIC_CONSUMER_SURFACE_ATTR = "data-hr-public-surface";

export function markPublicConsumerSurface(doc) {
  const root = doc || (typeof document !== "undefined" ? document : null);
  root?.documentElement?.setAttribute(PUBLIC_CONSUMER_SURFACE_ATTR, "1");
}

export function unmarkPublicConsumerSurface(doc) {
  const root = doc || (typeof document !== "undefined" ? document : null);
  root?.documentElement?.removeAttribute(PUBLIC_CONSUMER_SURFACE_ATTR);
}

function hasPublicConsumerSurfaceMarker(doc) {
  const root = doc || (typeof document !== "undefined" ? document : null);
  return root?.documentElement?.getAttribute(PUBLIC_CONSUMER_SURFACE_ATTR) === "1";
}

/**
 * Consumer shopping surfaces: public mini-site, vacation-packages, and the
 * entire TripMaster app. Agent-backoffice toasts/banners must not appear here.
 */
export function isPublicConsumerSearchSurface(pathname) {
  if (isTripMasterSurface()) return true;
  if (hasPublicConsumerSurfaceMarker()) return true;
  if (readRuntimePublicSiteIdentity()?.publicSiteToken) return true;
  const path =
    pathname != null
      ? pathname
      : typeof window !== "undefined"
        ? window.location?.pathname
        : "";
  return isPublicSiteRoute(path);
}

/** Agent-style search toasts stay on the agent backoffice only. */
export function shouldNotifyAgentSystemToasts(pathname) {
  return !isPublicConsumerSearchSurface(pathname);
}

export function extractPublicSiteRefFromSearch(search) {
  const params = new URLSearchParams(search || "");
  return firstNonEmpty(params.get("siteId"));
}

export function extractPublicSiteRefFromPath(pathname) {
  const match = String(pathname || "").match(/^\/agent\/([^/?#]+)/i);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export function extractPublicSiteSeedFromAgent(agent) {
  if (!agent || typeof agent !== "object") return null;
  const siteRef = normalizePublicSiteToken(
    agent?.public_site_token,
    agent?.publicSiteToken,
    agent?.site_token,
    agent?.siteToken,
    agent?.public_site_slug,
    agent?.publicSiteSlug,
    agent?.slug
  );
  if (!siteRef) return null;
  return {
    siteRef,
    resolvedAgentId: normalizePublicAgentId(agent?.id, agent?.agent_id, agent?.agentId),
    resolvedSlug: firstNonEmpty(agent?.public_site_slug, agent?.publicSiteSlug, agent?.slug),
  };
}

export function extractPublicSiteRefFromHost(hostname) {
  const host = String(hostname || "").trim().toLowerCase();
  const tripMasterToken = resolveTripMasterPublicSiteToken({ hostname: host });
  if (tripMasterToken) return tripMasterToken;
  if (!host || host === "localhost" || host === "127.0.0.1") return null;
  const suffix = ".hotelsriver.com";
  if (host.endsWith(suffix)) {
    const subdomain = host.slice(0, -suffix.length);
    if (!subdomain || ["www", "app", "admin", "api"].includes(subdomain)) return null;
    return subdomain;
  }
  if (["hotelsriver.com", "www.hotelsriver.com", "app.hotelsriver.com"].includes(host)) return null;
  return host;
}

export function clearPersistedPublicSiteIdentity() {
  if (typeof window === "undefined") return null;
  try {
    window.localStorage?.removeItem(PUBLIC_SITE_IDENTITY_STORAGE_KEY);
    for (const legacyKey of PUBLIC_SITE_IDENTITY_LEGACY_STORAGE_KEYS) {
      window.localStorage?.removeItem(legacyKey);
    }
    window.localStorage?.removeItem(PUBLIC_SITE_TOKEN_STORAGE_KEY);
    window.sessionStorage?.removeItem(PUBLIC_SITE_TOKEN_STORAGE_KEY);
  } catch {
    /* storage cleanup is best-effort */
  }
}

export function readPersistedPublicSiteIdentity(now = Date.now()) {
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage?.getItem(PUBLIC_SITE_IDENTITY_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const token = normalizePublicSiteToken(parsed?.publicSiteToken);
      const expiresAt = Number(parsed?.expiresAt);
      if (token && Number.isFinite(expiresAt) && expiresAt > now) {
        return {
          publicSiteToken: token,
          resolvedAgentId: parsed?.resolvedAgentId ?? null,
          metadata: parsed?.metadata && typeof parsed.metadata === "object" ? parsed.metadata : {},
          savedAt: Number(parsed?.savedAt) || null,
          expiresAt,
        };
      }
    }
    clearPersistedPublicSiteIdentity();
    return null;
  } catch {
    clearPersistedPublicSiteIdentity();
    return null;
  }
}

export function readRuntimePublicSiteIdentity() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.__HR_PUBLIC_SITE_IDENTITY_CONTEXT;
    if (!raw || typeof raw !== "object") return null;
    const siteRef = normalizePublicSiteToken(raw.resolvedSiteId, raw.publicSiteToken, raw.siteRef);
    if (!siteRef) return null;
    return {
      publicSiteToken: siteRef,
      resolvedAgentId: raw.resolvedAgentId ?? null,
      metadata: {
        slug: firstNonEmpty(raw.resolvedSlug, raw.slug),
      },
      source: firstNonEmpty(raw.source) || "runtime",
    };
  } catch {
    return null;
  }
}

export function writeRuntimePublicSiteIdentity(identity = null) {
  if (typeof window === "undefined") return;
  if (!identity || typeof identity !== "object") {
    delete window.__HR_PUBLIC_SITE_IDENTITY_CONTEXT;
    return;
  }
  const siteRef = normalizePublicSiteToken(
    identity.publicSiteToken,
    identity.resolvedSiteId,
    identity.siteRef
  );
  if (!siteRef) return;
  window.__HR_PUBLIC_SITE_IDENTITY_CONTEXT = {
    resolvedSiteId: siteRef,
    resolvedAgentId: identity.resolvedAgentId ?? null,
    resolvedSlug: firstNonEmpty(identity.resolvedSlug, identity.slug) || null,
    source: firstNonEmpty(identity.source) || "runtime",
  };
}

export function persistPublicSiteIdentityRef({ siteRef, siteId, siteIdentity, now = Date.now() }) {
  if (typeof window === "undefined") return;
  const ref = normalizePublicSiteToken(siteRef) || normalizePublicSiteToken(siteId);
  if (!ref) return;
  const metadata = {
    agencyName: firstNonEmpty(siteIdentity?.agency_name, siteIdentity?.agencyName),
    logo: firstNonEmpty(siteIdentity?.logo),
    slug: firstNonEmpty(siteIdentity?.slug),
  };
  const entry = {
    publicSiteToken: ref,
    resolvedAgentId: siteIdentity?.agent_id ?? siteIdentity?.agentId ?? null,
    metadata,
    savedAt: now,
    expiresAt: now + PUBLIC_SITE_IDENTITY_TTL_MS,
  };
  try {
    window.localStorage?.setItem(PUBLIC_SITE_IDENTITY_STORAGE_KEY, JSON.stringify(entry));
    window.localStorage?.setItem(PUBLIC_SITE_TOKEN_STORAGE_KEY, ref);
    window.sessionStorage?.setItem(PUBLIC_SITE_TOKEN_STORAGE_KEY, ref);
  } catch {
    // Storage is a cache only; bootstrap remains the source of truth.
  }
}

export function resolvePublicSiteBootstrapCandidate({
  search,
  pathname,
  hostname,
} = {}) {
  const runtimeIdentity = readRuntimePublicSiteIdentity();
  const hasRuntimeIdentity = Boolean(runtimeIdentity?.publicSiteToken);
  const hasCachedIdentity = Boolean(readPersistedPublicSiteIdentity()?.publicSiteToken);
  const fromSearch = extractPublicSiteRefFromSearch(search);
  if (fromSearch) {
    return {
      siteRef: normalizePublicSiteToken(fromSearch),
      invalidSiteRef: isNumericPublicSiteRef(fromSearch) ? fromSearch : null,
      source: "query",
      required: true,
      resolvedSlug: null,
      resolvedAgentId: null,
      hasCachedIdentity,
    };
  }

  const fromPath = extractPublicSiteRefFromPath(pathname);
  if (fromPath) {
    const asToken = normalizePublicSiteToken(fromPath);
    const asAgentId = normalizePublicAgentId(fromPath);
    return {
      siteRef: asToken,
      invalidSiteRef: null,
      pathAgentId: asToken ? null : asAgentId,
      source: "path",
      required: true,
      resolvedSlug: asToken || null,
      resolvedAgentId: asAgentId,
      hasCachedIdentity,
    };
  }

  const fromHost = extractPublicSiteRefFromHost(hostname);
  if (fromHost) {
    return {
      siteRef: fromHost,
      invalidSiteRef: null,
      source: "host",
      required: true,
      resolvedSlug: fromHost,
      resolvedAgentId: null,
      hasCachedIdentity,
    };
  }

  if (hasRuntimeIdentity) {
    return {
      siteRef: runtimeIdentity.publicSiteToken,
      invalidSiteRef: null,
      source: "runtime",
      required: true,
      persistedIdentity: runtimeIdentity,
      resolvedSlug: runtimeIdentity?.metadata?.slug || null,
      resolvedAgentId: runtimeIdentity?.resolvedAgentId ?? null,
      hasCachedIdentity,
    };
  }

  const stored = readPersistedPublicSiteIdentity();
  if (stored?.publicSiteToken) {
    return {
      siteRef: stored.publicSiteToken,
      invalidSiteRef: null,
      source: "localStorage",
      required: true,
      persistedIdentity: stored,
      resolvedSlug: stored?.metadata?.slug || null,
      resolvedAgentId: stored?.resolvedAgentId ?? null,
      hasCachedIdentity,
    };
  }

  return {
    siteRef: null,
    invalidSiteRef: null,
    pathAgentId: null,
    source: null,
    resolvedSlug: null,
    resolvedAgentId: null,
    hasCachedIdentity,
    required: isPublicSiteRoute(pathname),
  };
}

